import { httpClient } from "@bw/shared/api"
import type {
  MembershipPriceOverrideCreate,
  MembershipPriceOverrideResponse,
  PricingRuleCreate,
  PricingRuleMoveRequest,
  PricingRuleResponse,
  PricingRuleUpdate,
} from "@bw/shared/types"

// Rates client — a thin CRUD wrapper over the facility pricing-rules endpoints.
// The FE owns NO pricing logic: the backend owns price computation, eligibility,
// and precedence (the `priority` ordering). This client only forwards what the
// operator typed and returns what the backend sends.
//
// Backend contract (backend/app/routers/facility.py — router prefix "/facilities"):
//   GET    /facilities/{facility_id}/pricing-rules  -> List[PricingRuleResponse]  (server order)
//   POST   /facilities/{facility_id}/pricing-rules  body PricingRuleCreate  -> PricingRuleResponse
//   PUT    /facilities/pricing-rules/{rule_id}      body PricingRuleUpdate  -> PricingRuleResponse
//   DELETE /facilities/pricing-rules/{rule_id}      -> 204 (void)
//
// Types are derived from the generated OpenAPI types (@bw/shared/types) so they
// stay in lockstep with backend Pydantic. The generated money fields are branded
// `Money`; we widen them to `number | string` at this boundary because that is
// the actual wire shape (Pydantic Decimal serializes as a string) and money is
// rendered through the shared `formatMoney` helper, never via raw arithmetic.

type MoneyWire = number | string

// ---------------------------------------------------------------------------
// Enums (Literal unions mirroring the backend PricingRule* Pydantic Literals).
// The generated OpenAPI type widens these to bare `string`; we narrow them at
// this client boundary (mirroring leaguesApi's LeagueFormat) so the dialog's
// Select options and the body it forwards stay in lockstep with the backend's
// accepted values. Each union ships a const array the form maps over.
// ---------------------------------------------------------------------------

export type PricingService =
  | "court_reservation"
  | "open_play"
  | "classes"
  | "coaching"
  | "memberships"

export const PRICING_SERVICES: PricingService[] = [
  "court_reservation",
  "open_play",
  "classes",
  "coaching",
  "memberships",
]

export type ChargeUnit = "per_hour" | "per_session" | "flat"

export const CHARGE_UNITS: ChargeUnit[] = ["per_hour", "per_session", "flat"]

export type RepeatType = "never" | "daily" | "weekdays" | "weekends" | "weekly"

export const REPEAT_TYPES: RepeatType[] = [
  "never",
  "daily",
  "weekdays",
  "weekends",
  "weekly",
]

export type MembershipScope =
  | "any"
  | "non_member"
  | "any_member"
  | "specific_plans"

export const MEMBERSHIP_SCOPES: MembershipScope[] = [
  "any",
  "non_member",
  "any_member",
  "specific_plans",
]

// The enum-narrowed fields the dialog drives. Applied as an intersection over
// the generated Response/Create types so a backend rename surfaces here.
type PricingEnumFields = {
  service: PricingService
  charge_unit: ChargeUnit
  membership_scope: MembershipScope
  repeat_type: RepeatType
}

// `source` is server-defaulted ("advanced") — openapi-typescript marks a field
// with a default as REQUIRED, but on the wire the FE may omit it (the backend
// fills it) and a Response always carries it. We widen both to OPTIONAL at this
// client boundary so a fixture / create body that omits source type-checks,
// mirroring how the money fields are widened. The advanced editor still sends
// source="advanced" explicitly. `name` is the operator's optional display label.
export type PricingRule = Omit<
  PricingRuleResponse,
  | "base_price"
  | "entry_fee"
  | "extra_player_fee"
  | "service"
  | "charge_unit"
  | "membership_scope"
  | "repeat_type"
  | "source"
  | "name"
> &
  PricingEnumFields & {
    base_price: MoneyWire
    entry_fee?: MoneyWire | null
    extra_player_fee?: MoneyWire | null
    source?: string
    name?: string | null
  }

export type PricingRuleCreateInput = Omit<
  PricingRuleCreate,
  | "base_price"
  | "entry_fee"
  | "extra_player_fee"
  | "service"
  | "charge_unit"
  | "membership_scope"
  | "repeat_type"
  | "source"
  | "name"
  | "priority"
> &
  PricingEnumFields & {
    base_price: MoneyWire
    entry_fee?: MoneyWire | null
    extra_player_fee?: MoneyWire | null
    source?: string
    name?: string | null
    priority?: number
  }

export type PricingRuleUpdateInput = Omit<
  PricingRuleUpdate,
  | "base_price"
  | "entry_fee"
  | "extra_player_fee"
  | "service"
  | "charge_unit"
  | "membership_scope"
  | "repeat_type"
  | "source"
  | "name"
> &
  PricingEnumFields & {
    base_price: MoneyWire
    entry_fee?: MoneyWire | null
    extra_player_fee?: MoneyWire | null
    source?: string
    name?: string | null
  }

export function listPricingRules(facilityId: string): Promise<PricingRule[]> {
  return httpClient.get<PricingRule[]>(`/facilities/${facilityId}/pricing-rules`)
}

export function createPricingRule(
  facilityId: string,
  body: PricingRuleCreateInput,
): Promise<PricingRule> {
  return httpClient.post<PricingRule>(`/facilities/${facilityId}/pricing-rules`, body)
}

export function updatePricingRule(
  ruleId: string,
  body: PricingRuleUpdateInput,
): Promise<PricingRule> {
  return httpClient.put<PricingRule>(`/facilities/pricing-rules/${ruleId}`, body)
}

export function deletePricingRule(ruleId: string): Promise<void> {
  return httpClient.delete<void>(`/facilities/pricing-rules/${ruleId}`)
}

// Move one rule within the service-local order. The expected id sequence is an
// optimistic-concurrency token: the backend either applies the move against
// that exact service snapshot or returns 409 without changing priorities.
//
// Backend contract (backend/app/routers/facility.py — prefix "/facilities"):
//   POST /facilities/{facility_id}/pricing-rules/move
//        body PricingRuleMoveRequest
//        -> List[PricingRuleResponse]
export function movePricingRule(
  facilityId: string,
  ruleId: string,
  direction: "up" | "down",
  expectedServiceRuleIds: string[],
): Promise<PricingRule[]> {
  const body: PricingRuleMoveRequest = {
    rule_id: ruleId,
    direction,
    expected_service_rule_ids: expectedServiceRuleIds,
  }
  return httpClient.post<PricingRule[]>(
    `/facilities/${facilityId}/pricing-rules/move`,
    body,
  )
}

// ---------------------------------------------------------------------------
// Membership price overrides (F4 Slice 4) — a per-(rule, plan) member price
// that supersedes the rule's base_price for members on that plan. The FE owns
// NO pricing logic: the backend computes the actual price; this client only
// lists/creates/removes the override rows and renders member_price through the
// shared formatMoney helper (never raw arithmetic).
//
// Backend contract (backend/app/routers/facility.py — router prefix "/facilities"):
//   GET    /facilities/{facility_id}/price-overrides?rule_id={rule_id}
//                                                  -> List[MembershipPriceOverrideResponse]
//   POST   /facilities/{facility_id}/price-overrides  body MembershipPriceOverrideCreate
//                                                  -> MembershipPriceOverrideResponse
//   DELETE /facilities/price-overrides/{override_id}  -> 204 (void)
//
// DB UNIQUE(rule_id, plan_id) — a second override for the same (rule, plan)
// 409s; the caller surfaces that honestly (no phantom row).
//
// member_price is a Decimal: number | string at runtime (Pydantic serializes
// as a string for precision). We widen the generated branded `Money` to
// MoneyWire at this boundary — mirroring the rate-CRUD money fields above — so
// the component renders it through formatMoney with no coercion.

export type MembershipPriceOverride = Omit<
  MembershipPriceOverrideResponse,
  "member_price"
> & {
  member_price: MoneyWire
}

export type MembershipPriceOverrideCreateInput = Omit<
  MembershipPriceOverrideCreate,
  "member_price"
> & {
  member_price: MoneyWire
}

export function listPriceOverrides(
  facilityId: string,
  ruleId: string,
): Promise<MembershipPriceOverride[]> {
  return httpClient.get<MembershipPriceOverride[]>(
    `/facilities/${facilityId}/price-overrides?rule_id=${ruleId}`,
  )
}

export function createPriceOverride(
  facilityId: string,
  body: MembershipPriceOverrideCreateInput,
): Promise<MembershipPriceOverride> {
  return httpClient.post<MembershipPriceOverride>(
    `/facilities/${facilityId}/price-overrides`,
    body,
  )
}

export function deletePriceOverride(overrideId: string): Promise<void> {
  return httpClient.delete<void>(`/facilities/price-overrides/${overrideId}`)
}
